import React, { useEffect, useRef } from 'react';
import { LuCheck, LuMinus } from 'react-icons/lu';

/**
 * The wizard's checkbox — a 20px box, 5px round, in border/default-alt.
 *
 * The native input is kept, invisible, over the drawn box: it is what the
 * keyboard, a screen reader and a wrapping <label> all talk to. `indeterminate`
 * is a property rather than an attribute, so it is set on the element.
 *
 * The design draws the empty box only. Ticked, it fills with the brand and
 * carries a white tick — a dash when only some of what it stands for is ticked.
 * Those two glyphs are Lucide's because the file has none to export.
 */
const Checkbox = ({ checked, indeterminate = false, disabled = false, onChange, className = '', ...rest }) => {
    const inputRef = useRef(null);
    const isOn = checked || indeterminate;

    useEffect(() => {
        if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <span className={`tw-relative tw-inline-flex tw-size-[20px] tw-shrink-0 ${className}`}>
            <input
                ref={inputRef}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                className="tw-peer tw-absolute tw-inset-0 tw-m-0 tw-size-full tw-cursor-pointer tw-appearance-none tw-opacity-0 disabled:tw-cursor-not-allowed"
                {...rest}
            />
            <span
                aria-hidden
                className={
                    'tw-pointer-events-none tw-flex tw-size-full tw-items-center tw-justify-center tw-rounded-[5px] ' +
                    'tw-border-[1.6px] tw-border-solid tw-transition-colors ' +
                    'peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-v2-brand peer-focus-visible:tw-ring-offset-1 ' +
                    (isOn ? 'tw-border-v2-brand tw-bg-v2-brand tw-text-white' : 'tw-border-v2-student-box tw-bg-white')
                }
            >
                {indeterminate && <LuMinus size={14} strokeWidth={3} />}
                {!indeterminate && checked && <LuCheck size={14} strokeWidth={3} />}
            </span>
        </span>
    );
};

export default Checkbox;
